/*
 * Browsers cache a failed dynamic import per specifier for the page's lifetime,
 * so re-running the same import() can never recover. The retry-dynamic-imports
 * plugin in vite.config.js rewrites built dynamic imports to call this instead;
 * the cache-busting query makes the retry a new specifier, forcing a real fetch.
 *
 * That rewrite also suppresses Vite's __vitePreload wrapper, which is what
 * normally loads a lazy chunk's code-split CSS, so the plugin emits
 * globalThis.__chunkCssDeps and the stylesheets are loaded here instead.
 */

function loadStylesheet(href: string): Promise<void> {
    return new Promise((resolve) => {
        const alreadyLoaded = [
            ...document.querySelectorAll<HTMLLinkElement>(
                'link[rel="stylesheet"]',
            ),
        ].some((existing) => existing.href === href);
        if (alreadyLoaded) {
            resolve();
            return;
        }
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.onload = () => resolve();
        link.onerror = () => {
            console.error("Failed to load chunk stylesheet", href);
            resolve();
        };
        document.head.appendChild(link);
    });
}

export async function loadChunkCssDeps(moduleUrl: string): Promise<void> {
    const cssDeps = globalThis.__chunkCssDeps ?? {};
    const { origin, pathname } = new URL(moduleUrl);
    const match = Object.entries(cssDeps).find(([chunkFile]) =>
        pathname.endsWith(`/${chunkFile}`),
    );
    if (!match) {
        return;
    }
    const [chunkFile, cssFiles] = match;
    const root = (origin + pathname).slice(0, -chunkFile.length);
    await Promise.all(
        cssFiles.map((cssFile) => loadStylesheet(root + cssFile)),
    );
}

export function installRetryChunkImport(): void {
    globalThis.__retryChunkImport = async (specifier, importer) => {
        const url = new URL(specifier, importer).href;
        await loadChunkCssDeps(url);
        try {
            return await import(/* @vite-ignore */ url);
        } catch {
            const busted = new URL(url);
            busted.searchParams.set("t", Date.now().toString());
            console.log("retrying with cache busting", busted.href);
            return await import(/* @vite-ignore */ busted.href);
        }
    };
}
